/**
 * useSurveyCaptures — fetches all capture records for a processed session.
 *
 * @remarks
 * Loads up to 500 captures in a single page (MVP limit).
 * Only fires when sessionId is non-null.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { getSurveyApiError, surveyApi } from '../services/survey-api.service';
import type { SurveyCapture } from '../types/survey.types';

export function useSurveyCaptures(sessionId: string | null): {
  captures: SurveyCapture[];
  isLoading: boolean;
  error: string | null;
} {
  const [captures, setCaptures] = useState<SurveyCapture[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadingRef = useRef(false);

  const load = useCallback(async (id: string) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      const page = await surveyApi.listCaptures(id, 1, 500);
      setCaptures(page.captures);
    } catch (err) {
      setError(getSurveyApiError(err));
    } finally {
      loadingRef.current = false;
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!sessionId) {
      setCaptures([]);
      return;
    }
    void load(sessionId);
  }, [sessionId, load]);

  return { captures, isLoading, error };
}
